import React from "react";
import { createPortal } from "react-dom";
import { X, Upload } from "lucide-react";
import { colors, fonts } from "../../theme";
import { ASSUMPTIONS } from "../../lib/citations";

// Short "how the number was derived" formula shown in the hover tooltip on each loop row.
export const LOOP_FORMULA = {
  water: "Water % = harvested supply (rainwater + greywater) ÷ crop irrigation (ET) demand, capped at 100%.",
  energy: "Energy % = on-site generation/recovery that is time-matched to demand ÷ operational demand (grow-light + HVAC).",
  nutrient: "Nutrient loop closes (100%) once a locked amendment-recovery loop returns on-site nutrient; otherwise 0%.",
  carbon: "Carbon % = net avoided emissions ÷ avoidable emissions (conventional supply chain − design footprint).",
};

const num = (v) => (v == null ? "—" : Math.round(v).toLocaleString());

// Build the live numeric trace for a loop straight from the balance object (no invented values).
function buildRows(key, bal) {
  if (key === "water") {
    const w = bal.water || {};
    return [
      ["Crop irrigation demand (ET)", `${num(w.demand_L)} L/yr`, "Σ crop water-use intensity (L/kg) × projected yield"],
      ["Rainwater harvested", `${num(w.rain_L)} L/yr`, "precipitation × roof area × 0.80 runoff"],
      ["Greywater recovered", `${num(w.greywater_L)} L/yr`, "occupants × L/person·day × 365 × 0.90 capture"],
      ["Total supply", `${num(w.supply_L)} L/yr`, "rainwater + greywater"],
      [w.supply_L >= w.demand_L ? "Surplus" : "Unmet demand", `${num(Math.abs((w.supply_L || 0) - (w.demand_L || 0)))} L/yr`, null],
      ["Recovery", `${Math.round(((w.demand_L ? Math.min(1, (w.supply_L || 0) / w.demand_L) : 0)) * 100)}%`, "supply ÷ demand (capped at 100%)"],
    ];
  }
  if (key === "energy") {
    const e = bal.energy || {};
    const t = e.temporal || {};
    return [
      ["Operational demand", `${num(e.demand_kWh)} kWh/yr`, "grow-light + HVAC"],
      ["Solar PV", `${num(e.solar_kWh)} kWh/yr`, "irradiance × area × η × performance ratio"],
      ["Solar thermal", `${num(e.solar_thermal_kWh)} kWh/yr`, null],
      ["Recovered waste heat", `${num(e.wasteheat_kWh)} kWh/yr`, "exhaust heat × hours × effectiveness"],
      ["Biogas", `${num(e.biogas_kWh)} kWh/yr`, null],
      ["Parasitic load (resources)", `${num(e.parasitic_kWh)} kWh/yr`, null],
      ["Gross supply", `${num(e.gross_supply_kWh)} kWh/yr`, null],
      ["Time-matched supply", `${num(e.supply_kWh)} kWh/yr`, "supply only offsets demand in the same hours"],
      ["Day match", `${num(t.day?.met)} / ${num(t.day?.demand)} kWh`, "06:00–18:00"],
      ["Night match", `${num(t.night?.met)} / ${num(t.night?.demand)} kWh`, null],
      ["Offset", `${Math.round(e.offset || 0)}%`, "time-matched supply ÷ demand"],
    ];
  }
  if (key === "nutrient") {
    const n = bal.nutrient || {};
    return [
      ["Amendment recovered", `${num(n.amendment_kg)} kg/yr`, "on-site organics → compost / digestate / struvite"],
      ["Loop active", n.loop ? "Yes — locked amendment loop" : "No recovery loop locked", null],
      ["Status", n.loop ? "Closed (100%)" : "Open (0%)", "binary: closes once a locked amendment loop returns nutrient"],
    ];
  }
  // carbon
  const c = bal.carbon || {};
  return [
    ["Edible yield (on-site)", `${num(c.edible_yield_kg)} kg/yr`, null],
    ["Conventional chain mass", `${num(c.gross_conventional_kg)} kg/yr`, "extra mass grown to offset supply-chain loss"],
    ["Supply-chain loss", `${num(c.wasted_kg)} kg/yr`, `field + transit + distribution (${Math.round((c.loss_fraction || 0) * 100)}%)`],
    ["Food-miles (one-way)", `${num(c.food_miles_km)} km`, "weighted mean for imported produce (editable)"],
    ["Freight emission factor", c.freight_ef != null ? `${c.freight_ef} kg CO₂e/t·km` : "—", `reefer uplift +${Math.round(((c.reefer_uplift || 1) - 1) * 100)}%`],
    ["Grid factor", c.grid_factor_kg_per_kwh != null ? `${c.grid_factor_kg_per_kwh} kg/kWh` : "—", c.grid_province ? `${c.grid_province}${c.grid_auto ? " (auto)" : ""}` : null],
    ["Avoided emissions", `${num(c.avoided_kg)} kg CO₂e/yr`, "conventional chain avoided by growing on-site"],
    ["Design operational footprint", `${num(c.footprint_kg)} kg CO₂e/yr`, "this design's own GHG"],
    ["Net avoided", `${num(c.net_kg)} kg CO₂e/yr`, "avoided − footprint"],
    ["Carbon savings", `${Math.round(c.offset || 0)}%`, "net avoided ÷ avoidable"],
  ];
}

// Mini SVG sparkline of a loop's % at each committed-system count. Real recorded values only;
// when there are fewer than 2 points we invite the user to add more systems.
function LoopTrend({ history = [], closed }) {
  const pts = history.filter((h) => h.pct != null);
  if (pts.length < 2) {
    return (
      <div style={S.trendEmpty} data-testid="circ-trend-empty">
        {pts.length === 1
          ? `Currently ${pts[0].pct}% at ${pts[0].systems} system${pts[0].systems === 1 ? "" : "s"}. Add more systems to chart the shift.`
          : "Add systems to chart how this loop shifts."}
      </div>
    );
  }
  const W = 560, H = 96, padX = 30, padY = 14;
  const xs = pts.map((p) => p.systems);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const spanX = maxX - minX || 1;
  const px = (x) => padX + ((x - minX) / spanX) * (W - padX * 2);
  const py = (v) => H - padY - (Math.max(0, Math.min(100, v)) / 100) * (H - padY * 2);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${px(p.systems).toFixed(1)} ${py(p.pct).toFixed(1)}`).join(" ");
  const area = `${line} L ${px(maxX).toFixed(1)} ${H - padY} L ${px(minX).toFixed(1)} ${H - padY} Z`;
  const stroke = closed ? "#1f8f3b" : "#E08A1E";
  const first = pts[0], last = pts[pts.length - 1];
  const delta = last.pct - first.pct;
  return (
    <div data-testid="circ-trend">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: "block" }}>
        {[0, 50, 100].map((g) => (
          <line key={g} x1={padX} x2={W - padX} y1={py(g)} y2={py(g)} stroke="rgba(0,0,0,0.07)" strokeWidth="1" />
        ))}
        <path d={area} fill={`${stroke}1f`} stroke="none" />
        <path d={line} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={px(p.systems)} cy={py(p.pct)} r="3.5" fill="#fff" stroke={stroke} strokeWidth="2" />
            <text x={px(p.systems)} y={H - 2} textAnchor="middle" fontSize="9" fill="#9aa5ad" fontFamily="monospace">{p.systems}</text>
          </g>
        ))}
        <text x={px(last.systems)} y={Math.max(10, py(last.pct) - 8)} textAnchor="end" fontSize="11" fontWeight="700" fill={stroke} fontFamily="monospace">{last.pct}%</text>
      </svg>
      <div style={S.trendLegend}>
        <span>{first.pct}% at {first.systems} system{first.systems === 1 ? "" : "s"}</span>
        <span style={{ fontWeight: 700, color: delta >= 0 ? "#1f8f3b" : "#c0392b" }}>
          {delta >= 0 ? "▲ +" : "▼ "}{delta}% → {last.pct}% at {last.systems} systems
        </span>
      </div>
      <div style={S.trendNote}>x-axis = number of committed systems · recorded from your actual edits</div>
    </div>
  );
}

// Centered modal (portal) that traces exactly how a circularity loop % was computed, which systems
// & crops feed it, and the peer-reviewed references behind every conversion.
export function CircularityDetailModal({ loopKey, row, bal, systems = [], crops = [], history = [], onClose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!loopKey || !bal) return null;
  const a = ASSUMPTIONS[loopKey] || {};
  const rows = buildRows(loopKey, bal);
  const constants = a.constants || [];
  const refs = a.refs || [];
  const closed = row?.status === "closed";

  return createPortal(
    <div style={S.overlay} data-testid="circularity-detail-modal" onClick={onClose}>
      <div style={S.card} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div>
            <div style={S.title}>{row?.label || loopKey} · calculation trace</div>
            <div style={S.sub}>{a.title || "How this number was derived"}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ ...S.badge, ...(closed ? S.badgeOk : S.badgeWarn) }} data-testid="circ-detail-status">
              {loopKey === "carbon"
                ? `${closed ? "NET-POSITIVE" : "NET-NEGATIVE"} · ${row?.pct ?? 0}% savings`
                : `${closed ? "CLOSED" : "GAP"} · ${row?.pct ?? 0}%`}
            </span>
            <button style={S.close} data-testid="circularity-detail-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
          </div>
        </div>
        <div style={S.body}>
          {a.detail && <div style={S.formula}>{a.detail}</div>}
          <div style={S.formulaMono} data-testid="circ-detail-formula">{LOOP_FORMULA[loopKey]}</div>

          <div style={S.sectionLabel}>Trend as systems are added</div>
          <LoopTrend history={history} closed={closed} />

          <div style={S.sectionLabel}>Inputs & live numbers</div>
          {rows.map(([label, value, note], i) => (
            <div key={i} style={S.calcRow} data-testid={`circ-calc-${loopKey}-${i}`}>
              <div style={S.calcLeft}>
                <span style={S.calcLabel}>{label}</span>
                {note && <span style={S.calcNote}>{note}</span>}
              </div>
              <span style={S.calcValue}>{value}</span>
            </div>
          ))}

          <div style={S.sectionLabel}>Systems integrated ({systems.length})</div>
          <div style={S.chips}>
            {systems.length === 0 && <span style={S.muted}>No systems selected yet.</span>}
            {systems.map((s, i) => <span key={i} style={S.chip} data-testid={`circ-system-chip-${i}`}>{s}</span>)}
          </div>

          <div style={S.sectionLabel}>Crops ({crops.length})</div>
          <div style={S.chips}>
            {crops.length === 0 && <span style={S.muted}>No crops selected yet.</span>}
            {crops.map((c, i) => <span key={i} style={{ ...S.chip, ...S.chipCrop }} data-testid={`circ-crop-chip-${i}`}>{c}</span>)}
          </div>

          {constants.length > 0 && (
            <>
              <div style={S.sectionLabel}>Constants used</div>
              {constants.map(([name, val, src], i) => (
                <div key={i} style={S.calcRow}>
                  <div style={S.calcLeft}>
                    <span style={S.calcLabel}>{name}</span>
                    <span style={S.calcNote}>{src}</span>
                  </div>
                  <span style={S.calcValue}>{val}</span>
                </div>
              ))}
            </>
          )}

          <div style={S.sectionLabel}>References ({refs.length})</div>
          {refs.length === 0 && <span style={S.muted}>Binary loop — no external conversion factors.</span>}
          {refs.map((r, i) => (
            <a key={i} href={r.url} target="_blank" rel="noreferrer" style={S.refLink} data-testid={`circ-ref-${i}`}>
              {r.label} <Upload size={11} style={{ transform: "rotate(45deg)" }} />
            </a>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const S = {
  overlay: { position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,32,0.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card: { width: "min(640px, 94vw)", maxHeight: "85vh", display: "flex", flexDirection: "column", background: "#fff", borderRadius: 18, border: `1px solid ${colors.border}`, boxShadow: "0 24px 80px rgba(0,0,0,0.28)", overflow: "hidden" },
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "18px 22px", borderBottom: `1px solid ${colors.border}`, flexShrink: 0 },
  title: { fontFamily: fonts.sans, fontSize: 16, fontWeight: 800, letterSpacing: -0.3, color: colors.textPrimary },
  sub: { fontFamily: fonts.sans, fontSize: 12, color: colors.textSecondary, marginTop: 3 },
  badge: { padding: "3px 9px", borderRadius: 999, fontFamily: fonts.mono, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4 },
  badgeOk: { background: "rgba(52,199,89,0.15)", color: "#1f8f3b" },
  badgeWarn: { background: "rgba(224,138,30,0.15)", color: "#b06a0e" },
  close: { flexShrink: 0, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 999, border: `1px solid ${colors.border}`, background: "#fff", color: colors.textSecondary, cursor: "pointer" },
  body: { padding: "14px 22px 22px", overflowY: "auto" },
  formula: { fontFamily: fonts.sans, fontSize: 12.5, lineHeight: 1.55, color: colors.textSecondary, marginBottom: 10 },
  formulaMono: { fontFamily: fonts.mono, fontSize: 11, lineHeight: 1.5, color: colors.textPrimary, background: "rgba(0,0,0,0.04)", borderRadius: 9, padding: "9px 11px", marginBottom: 6 },
  sectionLabel: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 1, fontWeight: 700, color: colors.textTertiary, textTransform: "uppercase", margin: "16px 0 6px" },
  calcRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, padding: "7px 0", borderBottom: `1px solid ${colors.border}` },
  calcLeft: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  calcLabel: { fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 600, color: colors.textPrimary },
  calcNote: { fontFamily: fonts.sans, fontSize: 10.5, color: colors.textTertiary, lineHeight: 1.4 },
  calcValue: { flexShrink: 0, fontFamily: fonts.mono, fontSize: 12, fontWeight: 700, color: colors.textPrimary, textAlign: "right" },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: { padding: "4px 10px", borderRadius: 999, background: "rgba(81,170,254,0.12)", color: colors.brand, fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 600 },
  chipCrop: { background: "rgba(52,199,89,0.14)", color: "#1f8f3b" },
  muted: { fontFamily: fonts.sans, fontSize: 12, color: colors.textTertiary, fontStyle: "italic" },
  refLink: { display: "flex", alignItems: "center", gap: 6, padding: "6px 0", fontFamily: fonts.sans, fontSize: 12, color: colors.brand, textDecoration: "none", lineHeight: 1.4 },
  trendEmpty: { fontFamily: fonts.sans, fontSize: 12, color: colors.textTertiary, fontStyle: "italic", padding: "6px 0" },
  trendLegend: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 6, fontFamily: fonts.mono, fontSize: 10.5, color: colors.textSecondary },
  trendNote: { marginTop: 4, fontFamily: fonts.sans, fontSize: 10, color: colors.textTertiary, fontStyle: "italic" },
};
